//Built-in libraries

//Third party libraries

import { useCallback, useEffect, useId, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';
import { signApplicationPublicAsset } from '@/api/Rover/ApplicationForm';
//Custom components
import Button from '@/components/UI/Button';
import { HookFileInput } from '@/components/UI/HookForm/Form/File';
//Services
import { useApplicationFormContext } from '@/features/ApplicationForm/ApplicationFormProvider';
import { useFileUpload } from '@/hooks/useFileUpload';
import { notifyUser } from '@/store/Toast';
import type { FilesToUpload } from '@/types/Shared';
import { type CriticalRepairDocumentField, isApplicationFileMetadata } from '../fileMetadata';

//STP Libraries

//Assets and Miscellaneous

//Component Begin

export default function Documents() {
  const inputIdPrefix = useId();
  const { setCurrentStep } = useApplicationFormContext();
  const {
    watch,
    setValue,
    formState: { errors },
    trigger,
  } = useFormContext();
  const params = useParams();
  const dispatch = useDispatch();
  const { upload } = useFileUpload({ signAsset: signApplicationPublicAsset });
  const [pendingUploads, setPendingUploads] = useState<Partial<Record<CriticalRepairDocumentField, number>>>({});
  const [uploadErrors, setUploadErrors] = useState<Partial<Record<CriticalRepairDocumentField, string>>>({});
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const [isProcessingBankStatements, setIsProcessingBankStatements] = useState(false);

  // Watch for file changes
  const proofOfOwnership = watch('documents.proofOfOwnership');
  const proofOfOccupancy = watch('documents.proofOfOccupancy');
  const zeroIncomeAffidavit = watch('documents.zeroIncomeAffidavit');
  const bankStatements = watch('documents.bankStatements');
  const proofOfIncome = watch('documents.proofOfIncome');

  const isAnyUploadPending = Object.values(pendingUploads).some((count) => (count ?? 0) > 0);

  const updatePendingUploads = useCallback((fieldName: CriticalRepairDocumentField, difference: 1 | -1) => {
    setPendingUploads((current) => {
      const nextCount = Math.max(0, (current[fieldName] ?? 0) + difference);
      return { ...current, [fieldName]: nextCount };
    });
  }, []);

  // Helper to upload a file and set meta
  const uploadFileAndSetMeta = useCallback(
    async (fieldName: CriticalRepairDocumentField, file: File) => {
      updatePendingUploads(fieldName, 1);
      try {
        const signAssetPayload = [
          {
            formId: String(params.applicationFormId),
            fileName: file.name,
            fileType: file.type,
            fileSize: file.size,
            field: fieldName,
          },
        ];
        const { sign } = await upload({
          signAssetPayload,
          files: [file],
        });
        const uploadedFile: FilesToUpload = {
          name: file.name,
          path: sign.path,
          key: sign.key,
          type: file.type,
          field: fieldName,
          description: fieldName,
        };
        if (!isApplicationFileMetadata(uploadedFile)) {
          throw new Error('Upload signing response did not include complete file metadata');
        }

        // Set form value to metadata only (do not call addApplicationFile).
        if (fieldName === 'bankStatements') {
          // For bank statements, append to existing array
          const currentValue = watch(`documents.${fieldName}`) || [];
          // Filter out any File objects to avoid re-processing
          const existingFiles = currentValue.filter((item: unknown) => !(item instanceof File));
          setValue(`documents.${fieldName}`, [...existingFiles, uploadedFile], { shouldValidate: true });
        } else {
          // For other fields, replace the array
          setValue(`documents.${fieldName}`, [uploadedFile], { shouldValidate: true });
        }
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: `${file.name} uploaded successfully`,
            toastType: 'success',
          })
        );
      } catch (_err: unknown) {
        const currentValue = watch(`documents.${fieldName}`) || [];
        setValue(
          `documents.${fieldName}`,
          currentValue.filter((item: unknown) => item !== file),
          { shouldValidate: true }
        );
        const errorMessage = `Failed to upload ${file.name}. Select the file again to retry.`;
        setUploadErrors((prev) => ({ ...prev, [fieldName]: errorMessage }));
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: errorMessage,
            toastType: 'failed',
          })
        );
      } finally {
        updatePendingUploads(fieldName, -1);
      }
    },
    [dispatch, params.applicationFormId, setValue, updatePendingUploads, upload, watch]
  );

  // Effect for proofOfOwnership
  useEffect(() => {
    if (proofOfOwnership && Array.isArray(proofOfOwnership) && proofOfOwnership[0] instanceof File) {
      setUploadErrors((current) => ({ ...current, proofOfOwnership: undefined }));
      uploadFileAndSetMeta('proofOfOwnership', proofOfOwnership[0]);
    }
  }, [proofOfOwnership, uploadFileAndSetMeta]);

  // Effect for proofOfOccupancy
  useEffect(() => {
    if (proofOfOccupancy && Array.isArray(proofOfOccupancy) && proofOfOccupancy[0] instanceof File) {
      setUploadErrors((current) => ({ ...current, proofOfOccupancy: undefined }));
      uploadFileAndSetMeta('proofOfOccupancy', proofOfOccupancy[0]);
    }
  }, [proofOfOccupancy, uploadFileAndSetMeta]);

  // Effect for zeroIncomeAffidavit
  useEffect(() => {
    if (zeroIncomeAffidavit && Array.isArray(zeroIncomeAffidavit) && zeroIncomeAffidavit[0] instanceof File) {
      setUploadErrors((current) => ({ ...current, zeroIncomeAffidavit: undefined }));
      uploadFileAndSetMeta('zeroIncomeAffidavit', zeroIncomeAffidavit[0]);
    }
  }, [zeroIncomeAffidavit, uploadFileAndSetMeta]);

  // Effect for bankStatements (multiple files)
  useEffect(() => {
    if (
      bankStatements &&
      Array.isArray(bankStatements) &&
      bankStatements.length > 0 &&
      bankStatements.some((file) => file instanceof File) &&
      !isProcessingBankStatements
    ) {
      setIsProcessingBankStatements(true);
      setUploadErrors((current) => ({ ...current, bankStatements: undefined }));

      // Handle multiple files for bank statements
      const filesToUpload = bankStatements.filter((file) => file instanceof File);

      // Process files sequentially to avoid conflicts
      const processFiles = async () => {
        for (let i = 0; i < filesToUpload.length; i++) {
          const file = filesToUpload[i];
          await uploadFileAndSetMeta('bankStatements', file);
        }
        setIsProcessingBankStatements(false);
      };

      processFiles();
    }
  }, [bankStatements, uploadFileAndSetMeta, isProcessingBankStatements]);

  // Effect for proofOfIncome
  useEffect(() => {
    if (proofOfIncome && Array.isArray(proofOfIncome) && proofOfIncome[0] instanceof File) {
      setUploadErrors((current) => ({ ...current, proofOfIncome: undefined }));
      uploadFileAndSetMeta('proofOfIncome', proofOfIncome[0]);
    }
  }, [proofOfIncome, uploadFileAndSetMeta]);

  // Helper function to get validation error message
  const getValidationError = (fieldName: CriticalRepairDocumentField) => {
    if (errors.documents && showValidationErrors) {
      const documentsErrors = errors.documents as Partial<Record<CriticalRepairDocumentField, { message?: string }>>;
      return documentsErrors[fieldName]?.message;
    }
    return null;
  };

  // Helper function to get the single error message for a field (prioritizes upload errors)
  const getSingleErrorMessage = (fieldName: CriticalRepairDocumentField) => {
    // Priority 1: Upload errors
    const uploadError = uploadErrors[fieldName as keyof typeof uploadErrors];
    if (uploadError) {
      return uploadError;
    }

    // Priority 2: Form validation errors
    const validationError = getValidationError(fieldName);
    if (validationError) {
      return validationError;
    }

    // No errors
    return null;
  };

  const isFieldUploading = (fieldName: CriticalRepairDocumentField) => (pendingUploads[fieldName] ?? 0) > 0;

  return (
    <div className='sm:rounded-r-md sm:overflow-hidden'>
      <div className='py-6 px-4 space-y-6 sm:p-6'>
        <h3 className='text-lg leading-6 font-medium text-green-500'>Documents (Required)</h3>
        <div className='grid grid-cols-1 gap-6'>
          <div className='col-span-1'>
            <HookFileInput
              name='documents.proofOfOwnership'
              label='Proof of Ownership - Title Deed'
              id={`${inputIdPrefix}-proofOfOwnership`}
              multiple={false}
              disabled={isFieldUploading('proofOfOwnership')}
            />
            {getSingleErrorMessage('proofOfOwnership') && (
              <p className='mt-1 text-sm text-red-600'>{getSingleErrorMessage('proofOfOwnership')}</p>
            )}
          </div>
          <div className='col-span-1'>
            <HookFileInput
              name='documents.proofOfOccupancy'
              label='Proof of Occupancy - Utility Bill'
              id={`${inputIdPrefix}-proofOfOccupancy`}
              multiple={false}
              disabled={isFieldUploading('proofOfOccupancy')}
            />
            {getSingleErrorMessage('proofOfOccupancy') && (
              <p className='mt-1 text-sm text-red-600'>{getSingleErrorMessage('proofOfOccupancy')}</p>
            )}
          </div>
          <div className='col-span-1'>
            <HookFileInput
              name='documents.zeroIncomeAffidavit'
              label='Zero Income Affidavit (Optional)'
              id={`${inputIdPrefix}-zeroIncomeAffidavit`}
              multiple={false}
              disabled={isFieldUploading('zeroIncomeAffidavit')}
            />
            {getSingleErrorMessage('zeroIncomeAffidavit') && (
              <p className='mt-1 text-sm text-red-600'>{getSingleErrorMessage('zeroIncomeAffidavit')}</p>
            )}
          </div>
          <div className='col-span-1'>
            <HookFileInput
              name='documents.bankStatements'
              label='Bank Statements (Multiple files allowed)'
              id={`${inputIdPrefix}-bankStatements`}
              multiple={true}
              disabled={isFieldUploading('bankStatements') || isProcessingBankStatements}
            />
            {getSingleErrorMessage('bankStatements') && (
              <p className='mt-1 text-sm text-red-600'>{getSingleErrorMessage('bankStatements')}</p>
            )}
          </div>
          <div className='col-span-1'>
            <HookFileInput
              name='documents.proofOfIncome'
              label='Proof of Income (Optional)'
              id={`${inputIdPrefix}-proofOfIncome`}
              multiple={false}
              disabled={isFieldUploading('proofOfIncome')}
            />
            {getSingleErrorMessage('proofOfIncome') && (
              <p className='mt-1 text-sm text-red-600'>{getSingleErrorMessage('proofOfIncome')}</p>
            )}
          </div>
        </div>
        <div className='flex gap-4 justify-end mt-5'>
          <Button label='Previous' btnStyle='neutral' type='button' onClick={() => setCurrentStep((prev) => prev - 1)} />
          <Button
            label={isAnyUploadPending || isProcessingBankStatements ? 'Uploading files...' : 'Next'}
            btnStyle='primary'
            type='submit'
            disabled={isAnyUploadPending || isProcessingBankStatements}
            onClick={async () => {
              // Only show validation errors when user tries to proceed
              setShowValidationErrors(true);
              await trigger('documents');
            }}
          />
        </div>
      </div>
    </div>
  );
}
